import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { helpTopics, type HelpTopicSummary } from "@wmf/shared/help";
import { inboxLink } from "@wmf/shared/inboxLinks";
import { ApiError, get } from "../api.js";
import { slashQuery } from "../helpSlashQuery.js";

interface Match { kind: string; id: string; title: string; typeLabel: string; sourceLabel?: string; href: string }
interface Results { resources: Match[]; hasMore: boolean; nextCursor: string | null; topics?: HelpTopicSummary[] }

export function ReplyEditor({ id, threadId, value, onChange, disabled, audienceLabel = "colleague" }: {
  id: string; threadId: string; value: string; onChange: (value: string) => void; disabled: boolean; audienceLabel?: string;
}) {
  const textarea = useRef<HTMLTextAreaElement>(null);
  const listId = useId();
  const hintId = useId();
  const [trigger, setTrigger] = useState<ReturnType<typeof slashQuery>>(null);
  const [dismissed, setDismissed] = useState<string | null>(null);
  const [result, setResult] = useState<(Results & { key: string }) | null>(null);
  const [active, setActive] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [retry, setRetry] = useState(0);
  const [available, setAvailable] = useState(true);
  const searchKey = trigger ? `${threadId}:${trigger.start}:${trigger.kind ?? "resources"}:${trigger.topic ?? ""}:${trigger.browseTopics ? "topics" : trigger.query}` : null;
  const dismissKey = trigger?.browseTopics ? `${searchKey}:${trigger.topicQuery}` : searchKey;
  const open = available && searchKey !== null && dismissKey !== dismissed && !disabled;
  const current = result?.key === searchKey ? result : null;
  const latestSearch = useRef(searchKey);
  latestSearch.current = open ? searchKey : null;
  const options: Match[] = trigger?.browseTopics ? (current?.topics ?? []).filter(topic => {
    const query = (trigger.topicQuery ?? "").toLowerCase().replace(/-/g, " ");
    const definition = helpTopics.find(value => value.id === topic.id);
    return definition && [topic.id, topic.label, ...definition.aliases].some(word => word.toLowerCase().replace(/-/g, " ").includes(query));
  }).map(topic => ({ kind: "help_topic", id: topic.id, title: topic.label, href: "", typeLabel: `${topic.count} guide${topic.count === 1 ? "" : "s"}` }))
    : current?.resources ?? [];

  useEffect(() => () => { latestSearch.current = null; }, []);
  useEffect(() => { setAvailable(true); }, [threadId]);
  useEffect(() => { if (!value || disabled) setTrigger(null); }, [value, disabled]);
  useEffect(() => { setActive(0); }, [trigger?.topicQuery]);
  useEffect(() => {
    if (open && options[active]) document.getElementById(`${listId}-${active}`)?.scrollIntoView?.({ block: "nearest" });
  }, [open, active, listId, result]);
  useEffect(() => {
    setResult(null); setActive(0); setError(null); setLoadingMore(false);
    if (!open || !trigger || !searchKey) return;
    let live = true;
    const timer = window.setTimeout(() => {
      void get<Results>(`/inbox/threads/${encodeURIComponent(threadId)}/resources`, {
        q: trigger.query, kind: trigger.kind ?? "resources", includeTopics: trigger.browseTopics ? "1" : "0",
        ...(trigger.topic ? { topic: trigger.topic } : {}),
      }).then(data => { if (live) setResult({ ...data, key: searchKey }); })
        .catch((cause: unknown) => {
          if (!live) return;
          if (cause instanceof ApiError && cause.status === 404) { setAvailable(false); setTrigger(null); }
          else setError(cause instanceof Error ? cause.message : "Could not search links.");
        });
    }, 250);
    return () => { live = false; window.clearTimeout(timer); };
  }, [open, searchKey, retry, threadId]);

  function refresh() {
    if (!available) { setTrigger(null); return; }
    const field = textarea.current;
    const next = field && field.selectionStart === field.selectionEnd ? slashQuery(field.value, field.selectionStart) : null;
    // A stored [label](/help/slug) reference is already a link, not a command.
    setTrigger(next && field?.value.slice(next.start - 2, next.start) !== "](" ? next : null);
  }

  function choose(match: Match) {
    if (!trigger || disabled) return;
    try {
      const inserted = match.kind === "help_topic" ? `/help/${match.id} ` : `${inboxLink(match.title, match.href)} `;
      const next = value.slice(0, trigger.start) + inserted + value.slice(trigger.end);
      if (next.length > 5000) { setError("This link would make the message too long. Shorten the reply first."); return; }
      const cursor = trigger.start + inserted.length;
      onChange(next); setTrigger(match.kind === "help_topic" ? slashQuery(next, cursor) : null); setError(null);
      requestAnimationFrame(() => {
        textarea.current?.focus(); textarea.current?.setSelectionRange(cursor, cursor);
      });
    } catch { setError("That link is unavailable. Search again."); }
  }

  async function more(selectNext = false) {
    if (!current?.nextCursor || !trigger || !searchKey || loadingMore) return;
    const key = searchKey, firstNew = options.length;
    setLoadingMore(true); setError(null);
    try {
      const data = await get<Results>(`/inbox/threads/${encodeURIComponent(threadId)}/resources`, {
        q: trigger.query, kind: trigger.kind ?? "resources", cursor: current.nextCursor,
        ...(trigger.topic ? { topic: trigger.topic } : {}),
      });
      if (latestSearch.current !== key) return;
      setResult(previous => previous?.key === key ? { ...data, key, resources: [...previous.resources, ...data.resources] } : previous);
      if (selectNext && data.resources.length) setActive(firstNew);
    } catch (cause) {
      if (latestSearch.current === key) {
        if (cause instanceof ApiError && cause.status === 404) { setAvailable(false); setTrigger(null); }
        else setError(cause instanceof Error ? cause.message : "Could not load more links.");
      }
    }
    finally { if (latestSearch.current === key) setLoadingMore(false); }
  }

  function keyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (!open || event.nativeEvent.isComposing) return;
    if (event.key === "Escape") { event.preventDefault(); setDismissed(dismissKey); return; }
    if (event.key === "Tab") { setDismissed(dismissKey); return; }
    if (options.length && ["ArrowDown", "ArrowUp"].includes(event.key)) {
      event.preventDefault();
      const back = event.key === "ArrowUp";
      if (!back && active === options.length - 1 && current?.hasMore && !trigger?.browseTopics) { void more(true); return; }
      setActive(index => (index + (back ? -1 : 1) + options.length) % options.length);
    }
    if (event.key === "Enter") {
      if (options[active]) { event.preventDefault(); choose(options[active]); }
      else setDismissed(dismissKey);
    }
  }

  return <div className="inbox-link-editor" onBlur={event => {
    if (!event.currentTarget.contains(event.relatedTarget as Node)) setTrigger(null);
  }}>
    <textarea ref={textarea} id={id} value={value} maxLength={5000} rows={2} required disabled={disabled}
      aria-describedby={available ? hintId : undefined} aria-autocomplete={available ? "list" : undefined} aria-controls={open ? listId : undefined}
      aria-activedescendant={open && options[active] ? `${listId}-${active}` : undefined}
      onChange={event => { onChange(event.target.value); refresh(); }} onSelect={refresh} onClick={refresh} onKeyDown={keyDown} />
    {available && <p id={hintId} className="small muted">Type / for resources or /help for guides. Only links your {audienceLabel} can open are shown.<span className="visually-hidden"> Arrows choose; Enter inserts; Escape closes. Tab moves to the next control.</span></p>}
    {open && <div className="inbox-link-menu">
      <div id={listId} role="listbox" aria-label={trigger?.browseTopics ? "Help topics" : trigger?.kind === "help" ? "Matching help guides" : "Matching resources"}>
        {options.map((match, index) => <button type="button" role="option" id={`${listId}-${index}`} key={`${match.kind}:${match.href || match.id}`}
          aria-selected={index === active} onMouseDown={event => event.preventDefault()} onClick={() => choose(match)}>
          <span>{match.title}</span><span className="small muted">{match.typeLabel}{match.sourceLabel ? ` · ${match.sourceLabel}` : ""}</span>
        </button>)}
      </div>
      <p className="small" role="status">{error ?? (loadingMore ? "Loading more links…" : !current ? "Searching…" : options.length ? `${options.length} ${trigger?.browseTopics ? "topics" : "matching links"}` : "No matches available to both of you.")}</p>
      {current?.hasMore && !trigger?.browseTopics && <button type="button" className="quiet" disabled={loadingMore} onMouseDown={event => event.preventDefault()} onClick={() => void more()}>Load more matches</button>}
      {error && <button type="button" className="quiet" onClick={() => setRetry(version => version + 1)}>Retry search</button>}
    </div>}
  </div>;
}
